import React, { useState, useEffect } from 'react';
import { Target } from 'lucide-react';
import Critter from '../glyphs/Critter';

interface WhackAMoleGameProps {
  onComplete: (score: number) => void;
}

const MOLES = ['avatar_bear', 'avatar_fox', 'avatar_cat', 'avatar_panda', 'avatar_dog', 'avatar_koala'];

export default function WhackAMoleGame({ onComplete }: WhackAMoleGameProps) {
  const [moles, setMoles] = useState<boolean[]>(Array(9).fill(false));
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (!isActive) return;

    const timer = setInterval(() => {
      setTimeLeft((t) => Math.max(0, t - 1));
    }, 1000);

    const moleTimer = setInterval(() => {
      showRandomMole();
    }, 600); 

    return () => {
      clearInterval(timer);
      clearInterval(moleTimer);
    };
  }, [isActive]);

  useEffect(() => {
      if (timeLeft === 0 && isActive) {
          setIsActive(false);
          onComplete(score);
      }
  }, [timeLeft, isActive, onComplete, score]);

  const showRandomMole = () => {
    setMoles(() => {
       // Clear board first? Or just add one? Let's clear to avoid clustering too much
       const newState = Array(9).fill(false);
       const rand = Math.floor(Math.random() * 9);
       newState[rand] = true;
       return newState;
    });
  };

  const handleWhack = (index: number) => {
    if (moles[index] && isActive) {
      setScore(s => s + 5);
      // Hide mole immediately
      setMoles(current => {
          const newState = [...current];
          newState[index] = false;
          return newState;
      });
    }
  };

  const startGame = () => {
      setScore(0);
      setTimeLeft(30);
      setIsActive(true);
  };

  return (
    <div className="flex flex-col items-center justify-center h-full p-4">
      <div className="flex justify-between w-full max-w-sm mb-4 text-foreground items-end">
         <div>
             <h2 className="text-2xl font-bold flex items-center gap-2">
                 <Target className="text-red-500" /> פגע בחפרפרת
             </h2>
             <p className="text-muted-foreground text-sm">זמן: {timeLeft}s</p>
         </div>
         <div className="text-2xl font-bold text-yellow-500">{score}</div>
      </div>

      {!isActive ? (
          <div className="text-center">
              <p className="text-muted-foreground mb-8">נסו לפגוע בכמה שיותר מטרות ב-30 שניות!</p>
              <button 
                onClick={startGame}
                className="bg-red-600 hover:bg-red-500 text-white px-12 py-4 rounded-full font-bold text-xl shadow-lg transition-transform hover:scale-105"
            >
                התחל משחק
            </button>
          </div>
      ) : (
          <div className="grid grid-cols-3 gap-4">
            {moles.map((isUp, i) => (
                <button
                    key={i}
                    onClick={() => handleWhack(i)}
                    className={`w-24 h-24 rounded-full border-4 relative overflow-hidden transition-all duration-100 ${
                        isUp ? 'bg-red-500 border-red-700 cursor-pointer active:scale-90' : 'bg-muted border-border cursor-default'
                    }`}
                >
                    {isUp && (
                        <div className="absolute inset-0 flex items-center justify-center animate-bounce">
                            <Critter id={MOLES[i % MOLES.length]} size={56} />
                        </div>
                    )}
                </button>
            ))}
          </div>
      )}
    </div>
  );
}
